import { toHumanDuration } from "@/shared/components/HoursMinutesTextField";
import {
	labelCellStyle,
	tableCellStyle,
	tableHeaderStyle,
	wrapHeader,
} from "@/shared/utils/overview";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import {
	CampaignRowData,
	M1CurrentSectionData,
	M1UpcomingSectionData,
	UpcomingCampaignRowData,
} from "../models/M1Overview";

const CampaignRow = ({
	label,
	row,
}: {
	label: string;
	row: CampaignRowData | null;
}) => (
	<TableRow>
		<TableCell sx={labelCellStyle}>{label}</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.HeatNumber ?? "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.Grade ?? "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.ChargeTons ? parseFloat(row.ChargeTons.toFixed(2)) : "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.ChargeBilletsQty || "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.RolledTons != null ? parseFloat(row.RolledTons.toFixed(2)) : "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.RolledBillets ?? "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.TPH != null ? parseFloat(row.TPH.toFixed(2)) : "-"}
		</TableCell>
		<TableCell sx={tableCellStyle} align="center">
			{row?.Hours != null ? parseFloat(row.Hours.toFixed(2)) : "-"}
		</TableCell>
	</TableRow>
);

export const M1CurrentSectionInfo = ({
	data,
}: {
	data: M1CurrentSectionData;
}) => {
	const projectedRollOut = data.ProjectedRollOut
		? dxtToLocalServerTime(data.ProjectedRollOut, "MM/dd HH:mm")
		: "-";

	return (
		<Paper
			elevation={1}
			sx={{
				p: 1,
				border: "2px solid #4CAF50",
				borderRadius: 2,
				minWidth: 0,
				height: "100%",
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				<Typography
					variant="subtitle2"
					sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}
				>
					Current Section: {data.CurrentSectionName || "-"}
				</Typography>

				<Box sx={{ overflowX: "auto" }}>
					<Table
						size="small"
						sx={{
							"& .MuiTableCell-root": { py: 0.15, px: 0.2 },
						}}
					>
						<TableHead>
							<TableRow>
								<TableCell sx={tableHeaderStyle}></TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Heat #")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Grade")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Charged Tons")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Charged Billets Qty")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Rolled Tons")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Rolled Billets")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("TPH")}
								</TableCell>
								<TableCell sx={tableHeaderStyle} align="center">
									{wrapHeader("Hours")}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<CampaignRow label="Previous" row={data.Previous} />
							<CampaignRow label="Current" row={data.Current} />
						</TableBody>
					</Table>
				</Box>

				<Box sx={{ flexGrow: 1 }} />

				<Box
					sx={{
						mt: 0.5,
						display: "flex",
						gap: 3,
						flexWrap: "wrap",
						justifyContent: "space-between",
					}}
				>
					<Typography variant="caption" sx={{ color: "#666" }}>
						<em>Projected Roll Out:</em>{" "}
						<span style={{ color: "#d32f2f", fontWeight: 500 }}>
							{projectedRollOut}
						</span>
					</Typography>
					<Typography variant="caption" sx={{ color: "#666" }}>
						<em>Roll Change Time:</em>{" "}
						{data.RollChangeTime != null
							? toHumanDuration(data.RollChangeTime)
							: "-"}{" "}
						/{" "}
						{data.RollChangeScheduled != null
							? toHumanDuration(data.RollChangeScheduled)
							: "-"}{" "}
						Schd.
					</Typography>
				</Box>
			</Box>
		</Paper>
	);
};

const UpcomingRow = ({ row }: { row: UpcomingCampaignRowData | null }) => {
	return (
		<TableRow>
			<TableCell sx={tableCellStyle} align="center">
				{row ? parseFloat(row.ChargeTons.toFixed(2)) : "-"}
			</TableCell>
			<TableCell sx={tableCellStyle} align="center">
				{row?.ChargeBilletsQty || "-"}
			</TableCell>
			<TableCell sx={tableCellStyle} align="center">
				{row?.Hours != null ? parseFloat(row.Hours.toFixed(2)) : "-"}
			</TableCell>
			<TableCell sx={tableCellStyle} align="center">
				{row?.TargetTPH ?? "-"}
			</TableCell>
		</TableRow>
	);
};

export const M1UpcomingSectionInfo = ({
	data,
}: {
	data: M1UpcomingSectionData;
}) => {
	const projectedBreakin = data.ProjectedBreakin
		? dxtToLocalServerTime(data.ProjectedBreakin, "MM/dd HH:mm")
		: "-";

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<Typography
				variant="subtitle2"
				sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}
			>
				Upcoming Section: {data.UpcomingSectionName || "-"}
			</Typography>

			<Box sx={{ overflowX: "auto" }}>
				<Table
					size="small"
					sx={{
						minWidth: 400,
						"& .MuiTableCell-root": { py: 0.15, px: 0.5 },
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell sx={tableHeaderStyle} align="center">
								{wrapHeader("Charged. Tons")}
							</TableCell>
							<TableCell sx={tableHeaderStyle} align="center">
								{wrapHeader("Charged. Billets Qty.")}
							</TableCell>
							<TableCell sx={tableHeaderStyle} align="center">
								{wrapHeader("Hrs")}
							</TableCell>
							<TableCell sx={tableHeaderStyle} align="center">
								{wrapHeader("Target TPH")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<UpcomingRow row={data.First} />
					</TableBody>
				</Table>
			</Box>

			<Box sx={{ flexGrow: 1 }} />

			<Box
				sx={{
					mt: 0.5,
					display: "flex",
					gap: 3,
					flexWrap: "wrap",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="caption" sx={{ color: "#666" }}>
					<em>Proj. Breakin:</em>{" "}
					<span style={{ color: "#1565C0", fontWeight: 500 }}>
						{projectedBreakin}
					</span>
				</Typography>
				<Typography variant="caption" sx={{ color: "#666" }}>
					<em>Roll Change Time:</em>{" "}
					{data.RollChangeTime != null
						? toHumanDuration(data.RollChangeTime)
						: "-"}{" "}
					Schd.
				</Typography>
			</Box>
		</Box>
	);
};
